import _ from "lodash";
import React from "react";
import {
  PencilLineOutlined,
  CopyOutlined,
  DeleteOutlined,
  TeacherAddOutlined,
  StarOutlined,
} from "@toddle-edu/ds-icons";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";
import {
  isCustomRoleCreationAllowed,
  getIsAccountConfigurationEnabled,
} from "Utils/ACLStoreUtils";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
} from "Constants/permissionConstants";

export const ACTION_TYPE = {
  CUSTOM: "CUSTOM",
  TODDLE_DEFINED: "TODDLE_DEFINED",
};

export const ACTION_STATE = {
  ENABLED: "ENABLED",
  DISABLED: "DISABLED",
};

export const BEHAVIOUR_CATEGORY_MAX_DEPTH = 3;

export const ACCESSIBILITY_DETAIL_TYPE = {
  INCIDENT_DETAIL: "INCIDENT_DETAIL",
  CONFIDENTIAL_DETAIL: "CONFIDENTIAL_DETAIL",
};

export const ACCESSIBILITY_ACCESS_TYPE = {
  CAN_EDIT: "CAN_EDIT",
  CAN_VIEW: "CAN_VIEW",
  NO_ACCESS: "NO_ACCESS",
  REFERRED: "REFERRED",
};

export const ACCESSIBILITY_TYPE = {
  STAFF: "STAFF",
  INVOLVED_STUDENT: "INVOLVED_STUDENT",
  FAMILY_MEMBERS: "FAMILY_MEMBERS",
  HOMEROOM_ADVISOR: "HOMEROOM_ADVISOR",
  ROLE: "RNP_ROLE",
};

export const ACTION_ACCESSIBILITY_TYPE_LOCALE_MAP = {
  ADMINS: "common:administrator_plural",
  ALL_STAFF: "behaviour:admins_and_staff_members",
};

export const SEVERITY_UPDATE_TYPE = {
  COLOR: "color",
  NAME: "name",
};

export const SEVERITY_ERROR_CODE = {
  NAME_REQUIRED: "SEVERITY_NAME_REQUIRED",
  NAME_REPEATED: "MULTIPLE_SEVERITIES_CANNOT_HAVE_SAME_NAME",
};

export const RNP_MODAL_TYPE = {
  EDIT_ROLE: "EDIT_ROLE",
  DUPLICATE_ROLE: "DUPLICATE_ROLE",
  USERS_MAPPED: "USERS_MAPPED",
  MAP_USERS_TO_ROLE: "MAP_USERS_TO_ROLE",
  DELETE_ROLE: "DELETE_ROLE",
  CUSTOM_ROLE: "CUSTOM_ROLE",
  UPDATE_PERMISSIONS: "UPDATE_PERMISSIONS",
  DISCARD_CHANGES: "DISCARD_CHANGES",
  MARK_AS_DEFAULT: "MARK_AS_DEFAULT",
};

const getCurrentEditorConfig = ({ mode }) => {
  const isEditMode = mode === "edit";
  const labelId = isEditMode ? "LABEL" : "PREVIEW_SELECTABLE_LABEL";

  return {
    columns: [
      {
        id: labelId,
        label: "behaviour:sub_category",
        customAddIconTooltipLabel: "common:add",
        placeholderTexts: ["common:list_item_one"],
        style: {
          flex: 1,
        },
        variant: "MULTI_LEVEL",
        isRequired: false,
      },
    ],
    errorCheckFields: ["label"],
    errorCheckFieldLabels: {
      label: "common:list_item_is_missing",
    },
    allowedFilters: ["SEARCH_TEXT"],
    allowedBulkActions: [],
    showSidebar: false,
  };
};

export const getCurrentEditorConfigMemoized = _.memoize(
  params => getCurrentEditorConfig(params),
  params => JSON.stringify(params)
);

export const formatMultilevelCategoryToNodes = ({ multiLevelCategory }) => {
  const [rootCategories, restCategories] = _.partition(multiLevelCategory, {
    level: 0,
  });

  const rootCategory = _.first(rootCategories);

  if (!rootCategory) {
    return { nodes: [], rootNode: null };
  }

  const { label = rootCategory?.name, depth = rootCategory?.level, ...rest } =
    rootCategory || {};

  const dummyRootNode = {
    ...update(rest, {
      type: { $set: "NESTED_CHECKLIST" },
      parentId: { $set: null },
      isDummy: { $set: true },
    }),
    label,
    depth,
  };

  const updatedNodes = _.map(restCategories, node => {
    const updatedNode = {
      ..._.cloneDeep(node),
      depth: node.depth || node.level,
      type: "NESTED_CHECKLIST",
      label: node.label || node.name,
      parentId: node.parentId || node.parentCategory?.id,
      children: node.children || [],
    };

    return updatedNode;
  });

  return { nodes: [dummyRootNode, ...updatedNodes], rootNode: dummyRootNode };
};

export const formatMultilevelCategoryToNodesMemoized = _.memoize(
  params => formatMultilevelCategoryToNodes(params),
  params => JSON.stringify(params)
);

export const colorPalette = [
  { color: "#B0B0B0" },
  { color: "#47CEAC" },
  { color: "#26C7D9" },
  { color: "#8888F2" },
  { color: "#FFBB33" },
  { color: "#FF8DA6" },
  { color: "#FF8D38" },
  { color: "#A9D844" },
  { color: "#C88AE8" },
];

export const getColorPaletteOptions = () => colorPalette;

export const getNomenclatureOptions = key => {
  const options = {
    BEHAVIOUR_MODULE_NOMENCLATURE_MODULE: [
      "Behaviour management",
      "Pastoral care",
      "Conduct management",
      "Well-being",
    ],
    BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT: [
      "Note",
      "Incident",
      "Conduct",
      "Case",
      "Record",
      "Infraction",
    ],
    BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL: [
      "Severity",
      "Intensity",
      "Degree",
      "Level",
      "Classification",
    ],
    BEHAVIOUR_MODULE_NOMENCLATURE_ACTION: [
      "Action",
      "Consequence",
      "Intervention",
      "Response",
    ],
  };

  const optionMap = label => ({ label: label, value: label });

  return _.map(options[key], optionMap);
};

export const getTerminologyConfig = () => {
  return [
    {
      key: "BEHAVIOUR_MODULE_NOMENCLATURE_MODULE",
      label: "common:module",
      subText: "behaviour:personalize_module_name",
    },
    {
      key: "BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT",
      label: "behaviour:incident",
      subText: "behaviour:customize_incidents_label",
    },
    {
      key: "BEHAVIOUR_MODULE_NOMENCLATURE_ACTION",
      label: "common:action",
      subText: "behaviour:customize_actions_label",
    },
    {
      key: "BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL",
      label: "common:level",
      subText: "behaviour:customize_severity_label",
    },
  ];
};

export const getBehaviourNomenclatureFilters = ({ organizationId }) => {
  return {
    organizationId,
    filter: {
      names: [
        "BEHAVIOUR_MODULE_NOMENCLATURE_ACTION",
        "BEHAVIOUR_MODULE_NOMENCLATURE_LEVEL",
        "BEHAVIOUR_MODULE_NOMENCLATURE_INCIDENT",
        "BEHAVIOUR_MODULE_NOMENCLATURE_MODULE",
      ],
    },
  };
};

export const getBehaviourNomenclatureFiltersMemoized = _.memoize(
  getBehaviourNomenclatureFilters,
  JSON.stringify
);

export const getIsSentimentWiseSeverity = () =>
  ACLStore.can("Behaviour:SentimentWiseSeverity");

const getSeverityFilters = ({
  categories,
  categoryId,
  isSentimentWiseSeverity,
}) => {
  const selectedCategoryData = _.find(
    categories,
    category => category?.id === categoryId
  );

  const sentimentId = _.get(selectedCategoryData, "sentiment.id");
  const severityFilters = isSentimentWiseSeverity
    ? {
        sentiments: [sentimentId],
      }
    : {};

  return severityFilters;
};

export const getSeverityFiltersMemoized = _.memoize(
  params => getSeverityFilters(params),
  params => JSON.stringify(params)
);

export const getIsBehaviourSetupFeedEnhancementEnabled = () =>
  ACLStore.can("Behaviour:CategorySetupFeedEnhancements");

export const getIsRnpEnableInBehaviour = () => {
  return ACLStore.can(
    [
      ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
      ENABLE_RNP_CONSUMPTION_IN_BEHAVIOUR,
    ],
    "all"
  );
};

export const getRnpRoleLabel = ({
  rnpRoles,
  accessibilityEntityId,
  t,
  getEntityLabelAndDescription, // Note: Not importing this function here due to circular dependency
}) => {
  const label = _.find(rnpRoles, { id: accessibilityEntityId })?.label;

  return label ? getEntityLabelAndDescription({ t, localeKey: label }) : null;
};

export const RNP_LEVEL = {
  ACCOUNT: "ACCOUNT",
  PROGRAMME: "PROGRAMME",
};

export const BEHAVIOUR_RNP_LEVELS = ["ACCOUNT", "PROGRAMME"];

export const getBehaviourRnPFeedVariablesMemoized = _.memoize(
  ({ organizationId, academicYearId }) => ({
    id: organizationId,
    rnpViewFilters: {
      rnpLevels: BEHAVIOUR_RNP_LEVELS,
      showBehaviourRoles: true,
    },
    academicYearId,
  }),
  ({ organizationId, academicYearId }) => `${organizationId}-${academicYearId}`
);

export const getGlobalLevels = ({ roleLevel }) => {
  if (!roleLevel) {
    return ["ACCOUNT", "PROGRAMME"];
  }

  return [roleLevel];
};

export const getRnPPermissionsFeedQueryVariablesMemoized = _.memoize(
  ({ organizationId, roleId, roleLevel }) => {
    const globalLevels = getGlobalLevels({ roleLevel });

    return {
      id: organizationId,
      roleId,
      permissionListFilters: {
        globalLevels,
        showBehaviourPermissions: true,
      },
    };
  },
  ({ organizationId, roleId, roleLevel }) =>
    _.join([organizationId, roleId, roleLevel], "_")
);

export const getRnPRoleDetailsVariablesMemoized = _.memoize(
  ({ organizationId, roleId }) => {
    return {
      id: organizationId,
      rnpViewFilters: {
        rnpRoleId: roleId,
        showBehaviourRoles: true,
      },
    };
  },
  ({ organizationId, roleId }) => _.join([organizationId, roleId], "_")
);

export const getBehaviourOrgStaffFeedQueryVariablesMemoized = _.memoize(
  ({ searchText, orderBy, orderByDirection }) => {
    return {
      first: 1000000,
      filters: { searchText },
      orderBy,
      orderByDirection,
      roleContextFilters: { roleModule: ["BEHAVIOUR"] },
    };
  },
  ({ searchText, orderBy, orderByDirection }) =>
    _.join([searchText, orderBy, orderByDirection], "::")
);

export const getActionMenuOptions = ({ role, t }) => {
  const {
    assignedUserCount,
    isSuperAdmin = false,
    isCustomRole = false,
    globalLevel,
    isDefaultRole = false,
  } = role;

  const getDeleteDisabledTooltip = () => {
    if (isDefaultRole) {
      return t("behaviour:cannot_delete_default_role");
    }
    if (!isCustomRole) {
      return t("acl:delete_role_disabled_action_tooltip");
    }
    return t("behaviour:delete_role_mapped_staff_disabled_action_tooltip");
  };

  const deleteDisabledTooltip = getDeleteDisabledTooltip();

  const programmeLabel = getProgrammeLabel({ t });

  const tabs = [
    {
      key: "editDescription",
      label: t("common:edit_with_label", {
        label: t("common:description"),
      }),
      icon: <PencilLineOutlined />,
      disabled: isSuperAdmin,
      disabledTooltip: t("acl:edit_description_disabled_action_tooltip"),
      tooltipPlacement: "left",
      visible: !isCustomRole,
    },
    {
      key: "editNameAndDescription",
      label: t("common:edit_with_label", {
        label: t("common:name_and_description"),
      }),
      icon: <PencilLineOutlined />,
      visible: isCustomRole,
    },
    {
      key: "duplicate",
      label: t("common:duplicate"),
      icon: <CopyOutlined />,
      visible: isCustomRoleCreationAllowed(),
    },
    {
      key: "manageMembers",
      label: t("common:manage_with_label", { label: t("common:members") }),
      icon: <TeacherAddOutlined />,
      visible: true,
    },
    {
      key: "MARK_AS_DEFAULT",
      label: t("behaviour:mark_as_programme_default", { programmeLabel }),
      icon: <StarOutlined />,
      visible: globalLevel === "PROGRAMME",
      disabled: isDefaultRole,
      disabledTooltip: t("behaviour:role_already_marked_as_default"),
      tooltipPlacement: "left",
    },
    {
      key: "delete",
      label: t("common:delete"),
      icon: <DeleteOutlined />,
      isDestructive: true,
      visible: true,
      disabled: !isCustomRole || assignedUserCount > 0 || isDefaultRole,
      disabledTooltip: deleteDisabledTooltip,
      tooltipPlacement: "bottom",
    },
  ];

  return _.filter(tabs, "visible");
};

export const onActionItemClick = ({ key, role, updateModalConfig }) => {
  switch (key) {
    case "editNameAndDescription":
    case "editDescription":
      updateModalConfig({ type: RNP_MODAL_TYPE.EDIT_ROLE, selectedRole: role });
      break;
    case "duplicate":
      updateModalConfig({
        type: RNP_MODAL_TYPE.DUPLICATE_ROLE,
        selectedRole: role,
      });
      break;
    case "delete":
      updateModalConfig({
        type: RNP_MODAL_TYPE.DELETE_ROLE,
        selectedRole: role,
      });
      break;
    case "manageMembers":
      updateModalConfig({
        type: RNP_MODAL_TYPE.MAP_USERS_TO_ROLE,
        selectedRole: role,
      });
      break;
    case "MARK_AS_DEFAULT":
      updateModalConfig({
        type: RNP_MODAL_TYPE.MARK_AS_DEFAULT,
        selectedRole: role,
      });
      break;
    default:
      break;
  }
};

export const getRnPLevel = ({ globalLevel }) => {
  if (globalLevel === "ACCOUNT") {
    return "ACCOUNT_LEVEL";
  }

  if (globalLevel === "PROGRAMME") {
    return "PROGRAMME_LEVEL";
  }

  return null;
};

export const getProgrammeLabel = ({ t }) => {
  return getIsAccountConfigurationEnabled()
    ? t("common:school")
    : t("common:programme");
};
